import { Button } from '@praetorian-chariot/ui';
import {
  Dispatch,
  SetStateAction,
  useMemo,
  useOptimistic,
  useState,
} from 'react';
import { toast } from 'sonner';

import { PraetorianSettingsContent } from '@/components/PraetorianSettingsContent';
import { ToggleSwitch } from '@/components/ui';
import { useMy } from '@/hooks';
import { useUpdateFlags } from '@/hooks/useFlags';
import { ChariotModuleFlags, FlagType } from '@/types';
import { cn } from '@/utils/classname';

const chariotModuleOptions = [
  {
    name: 'Static IP Addresses',
    description: 'Enables scanning from a static IP pool',
    value: FlagType.DISABLE_STATIC_IP_ADDRESSES,
  },
  {
    name: 'AI Training',
    description: 'Enables AI Training on data from this account',
    value: FlagType.DISABLE_AIMI_TRAINING,
  },
];

export const ChariotModules = ({
  isPraetorianUser = false,
  customerEmail,
  onClose,
  onDeleteAccount,
}: {
  isPraetorianUser: boolean;
  customerEmail?: string;
  onClose?: () => void;
  onDeleteAccount?: () => void;
}) => {
  const { data: flagsData, isLoading: isLoadingFlags } = useMy(
    {
      resource: 'flag',
    },
    { email: customerEmail }
  );
  const { mutateAsync: updateFlags } = useUpdateFlags({
    email: customerEmail,
  });

  /**
   * State management strategy using React 19's useOptimistic hook:
   * - serverFlags: Derived from API data (single source of truth)
   * - optimisticFlags: Managed by useOptimistic for instant UI updates
   *
   * Benefits of useOptimistic:
   * - Automatic rollback on errors (no manual state management)
   * - Simpler mental model (React handles state transitions)
   * - No manual reset logic needed (React syncs with serverFlags)
   * - Type-safe and battle-tested by React team
   *
   * This replaces the previous two-state pattern (serverFlags + localFlags)
   * which required manual synchronization and error handling.
   */

  // Derive server flags from API data - single source of truth
  const serverFlags = useMemo(
    () =>
      (flagsData || [])
        .map(flag => flag.name)
        .filter(name => ChariotModuleFlags.includes(name as FlagType)),
    [flagsData]
  );

  // Use React 19's useOptimistic for automatic optimistic updates and error rollback
  const [optimisticFlags, setOptimisticFlags] = useOptimistic(
    serverFlags,
    (_currentState, optimisticValue: string[]) => optimisticValue
  );

  // Wrapper to match Dispatch<SetStateAction<string[]>> type expected by child components
  const handleSetFlags = (value: SetStateAction<string[]>) => {
    if (typeof value === 'function') {
      setOptimisticFlags(value(optimisticFlags));
    } else {
      setOptimisticFlags(value);
    }
  };

  return (
    <>
      <div className={cn('mb-3 flex flex-col gap-6')}>
        {/* Section Header */}
        <div>
          <h2 className="text-chariot-text-primary mb-1 flex items-center gap-2 text-2xl font-bold">
            <img src="/icons/logo.png" alt="logo" className="size-5" />
            Praetorian Only Section
          </h2>
          <p className="text-chariot-text-secondary text-sm">
            Configure advanced features and settings for this account.
          </p>
        </div>

        {/* Main Container */}
        <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
          <PraetorianSettingsContent
            customerEmail={customerEmail}
            isPraetorianUser={isPraetorianUser}
            isLoadingFlags={isLoadingFlags}
            flags={optimisticFlags}
            setFlags={handleSetFlags}
            updateFlags={updateFlags}
            prevFlags={serverFlags}
            onDeleteAccount={onDeleteAccount}
            showDeleteAccount={true}
            showPlexTrac={!!customerEmail}
          />
        </div>

        {customerEmail && (
          <div className="flex justify-end">
            <Button
              styleType="secondary"
              label="Close"
              onClick={async () => {
                onClose?.();
              }}
              data-testid="chariot-modules-close-button"
            />
          </div>
        )}
      </div>
    </>
  );
};

export const ChariotModulesOptions = ({
  isLoadingFlags,
  flags,
  setFlags,
  readOnly,
  className,
  checkboxClassName: _checkboxClassName,
  updateFlags,
  prevFlags: _prevFlags,
  disabledFlags = [],
}: {
  isLoadingFlags: boolean;
  flags: string[];
  setFlags?: Dispatch<SetStateAction<string[]>>;
  readOnly?: boolean;
  className?: string;
  checkboxClassName?: string;
  updateFlags?: (params: {
    flagsToAdd?: string[];
    flagsToDelete?: string[];
    successMessage?: string;
    silent?: boolean;
  }) => Promise<unknown>;
  prevFlags?: string[];
  disabledFlags?: string[];
}) => {
  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);

  const handleToggle = async (
    checked: boolean,
    value: string,
    name: string
  ) => {
    if (readOnly || !updateFlags || disabledFlags.includes(value)) {
      return;
    }

    setUpdatingFlag(value);

    try {
      const newFlags = checked
        ? flags.filter(flag => flag !== value)
        : [...flags, value];

      // Optimistic update - useOptimistic will auto-rollback on error
      setFlags?.(newFlags);

      const flagsToAdd = !checked ? [value] : [];
      const flagsToDelete = checked ? [value] : [];

      if (value === FlagType.DISABLE_STATIC_IP_ADDRESSES) {
        if (checked) {
          flagsToAdd.push('static');
        } else {
          flagsToDelete.push('static');
        }
      }

      await updateFlags({
        flagsToAdd,
        flagsToDelete,
        silent: true, // ✅ Suppress hook toast for custom message
      });

      toast.success(`${name} has been ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // useOptimistic automatically rolls back to serverFlags on error
      // No manual rollback needed!
      toast.error(`Failed to update ${name}. Please try again.`);
      console.error('Failed to update flag:', error);
    } finally {
      setUpdatingFlag(null);
    }
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {chariotModuleOptions.map(({ name, description, value }) => {
        const isUpdating = updatingFlag === value;
        const isEnabled = !flags.includes(value);
        const isDisabled = disabledFlags.includes(value);

        return (
          <div
            key={value}
            className={cn(
              'border-chariot-stroke rounded-md border p-4',
              (readOnly || isDisabled) && 'opacity-60'
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h3
                  className={cn(
                    'text-chariot-text-primary text-base font-bold',
                    (readOnly || isDisabled) && 'text-chariot-text-secondary'
                  )}
                >
                  {name}
                </h3>
                <p className="text-chariot-text-secondary text-sm">
                  {description}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  id={`feature-${value}`}
                  checked={isEnabled}
                  disabled={
                    isLoadingFlags || readOnly || isUpdating || isDisabled
                  }
                  onChange={checked => handleToggle(checked, value, name)}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
